import { useEffect, useState, useCallback } from "react";
import { arbitrageApi } from "@/lib/api-client";
import { SovereignRFQCard } from "./SovereignRFQCard";
import { TrendingUp, Radar, Loader2, Zap, Clock, Percent, CheckCircle2, Landmark } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  proposed: "PROPOSED", approved: "EXECUTING", delivered: "DELIVERED",
  awaiting_platform: "SEALED · AWAITING PLATFORM", blocked: "BLOCKED", rejected_guardrail: "GUARDRAIL REJECT",
};

export default function RevenueTerminal() {
  const [summary, setSummary] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [taskRfqs, setTaskRfqs] = useState({}); // task_id -> pending RFQ awaiting sign-off

  const load = useCallback(async () => {
    try {
      const [s, t] = await Promise.all([arbitrageApi.summary(), arbitrageApi.tasks()]);
      setSummary(s);
      setTasks(t);
    } catch (e) { console.error("revenue terminal load failed", e); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, [load]);

  const scan = async () => {
    setScanning(true);
    try {
      const r = await arbitrageApi.scan();
      toast.success(`Scout found ${r.proposed} guardrail-cleared gigs (${r.rejected_by_guardrail} rejected)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Arbitrage scan failed");
    } finally { setScanning(false); }
  };

  const approve = async (id, rfqId) => {
    setBusyId(id);
    try {
      const r = await arbitrageApi.approve(id, rfqId);
      if (r.rfq_required) {
        // GUARDRAIL — cross-venture movement locked until the Sovereign RFQ is signed off
        setTaskRfqs((m) => ({ ...m, [id]: r.rfq }));
        toast.info("GUARDRAIL — sign off the Sovereign RFQ to unlock execution");
        return;
      }
      const d = r.task?.delivery || {};
      toast.success(d.delivered
        ? `Executed & delivered — $${r.ledger_entry.profit.toFixed(2)} pending profit logged`
        : `Executed & sealed in vault — $${r.ledger_entry.profit.toFixed(2)} pending profit logged`);
      setTaskRfqs((m) => { const n = { ...m }; delete n[id]; return n; });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Execution failed");
    } finally { setBusyId(null); }
  };

  const markPaid = async (entryId) => {
    try {
      const r = await arbitrageApi.markPaid(entryId);
      toast.success(`$${r.profit.toFixed(2)} confirmed — Net Worth Pulse updated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Confirmation failed");
    }
  };

  const proposed = tasks.filter((t) => t.status === "proposed");
  const executed = tasks.filter((t) => ["delivered", "awaiting_platform"].includes(t.status)).slice(0, 5);

  return (
    <section className="uc-card p-6 uc-fade" data-testid="revenue-terminal">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="uc-chip mb-2"><TrendingUp size={11} className="text-emerald-400"/> UMBRELLA ARBITRAGE · REVENUE TERMINAL</div>
          <h2 className="font-serif-display text-2xl">Live earnings, as they land.</h2>
          <p className="text-white/50 text-xs mt-1 max-w-xl">
            Scout hunts ≤30-min digital gigs at ≥40% margin. On your approval, Creator + Executor
            do the work; Controller logs every cent — pending until you mark it paid.
          </p>
        </div>
        <button
          data-testid="arbitrage-scan-btn"
          onClick={scan}
          disabled={scanning}
          className="flex items-center gap-2 font-mono-uc text-[10px] uppercase tracking-widest text-emerald-400 hover:text-emerald-200 px-3 py-2 border border-emerald-500/40 hover:bg-emerald-500/10 disabled:opacity-50"
        >
          {scanning ? <Loader2 size={12} className="animate-spin"/> : <Radar size={12}/>}
          {scanning ? "Hunting…" : "Scan for Arbitrage"}
        </button>
      </div>

      {/* P&L stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { k: "PENDING", v: summary?.pending ?? 0, tid: "arb-pending", cls: "text-amber-400" },
          { k: "CONFIRMED", v: summary?.confirmed ?? 0, tid: "arb-confirmed", cls: "text-emerald-400" },
          { k: "TODAY", v: summary?.today ?? 0, tid: "arb-today", cls: "text-white/90" },
        ].map((s) => (
          <div key={s.k} className="border border-white/10 bg-black p-3">
            <div className="font-mono-uc text-[9px] uppercase tracking-widest text-white/40">{s.k}</div>
            <div className={`font-serif-display text-2xl mt-0.5 ${s.cls}`} data-testid={s.tid}>
              ${Number(s.v).toFixed(2)}
            </div>
          </div>
        ))}
      </div>

      {/* Proposed gigs */}
      {proposed.length > 0 && (
        <div className="space-y-2 mb-4" data-testid="arb-proposed-list">
          <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/40">Guardrail-Cleared Proposals</div>
          {proposed.map((t) => (
            <div key={t.id} className="border border-white/10 border-l-2 border-l-emerald-500/70 bg-white/[0.02] p-3" data-testid={`arb-task-${t.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[12px] text-white/90 font-medium">{t.title}</span>
                <span className="font-mono-uc text-[9px] text-teal-300 uppercase">{t.platform}</span>
              </div>
              <p className="text-[11px] text-white/55 mt-1 line-clamp-2">{t.brief}</p>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <span className="uc-chip text-[9px] flex items-center gap-1"><Clock size={9}/>{t.est_minutes}m</span>
                <span className="uc-chip text-[9px] text-emerald-400">${t.est_payout_usd.toFixed(2)} payout</span>
                <span className="uc-chip text-[9px] text-white/50">−${t.est_credit_cost.toFixed(2)} credits</span>
                <span className="uc-chip text-[9px] text-emerald-400 flex items-center gap-1 border-emerald-500/40">
                  <Percent size={9}/>{Math.round(t.margin * 100)}% margin
                </span>
                <button
                  data-testid={`arb-approve-${t.id}`}
                  onClick={() => approve(t.id)}
                  disabled={busyId === t.id || !!taskRfqs[t.id]}
                  className="ml-auto flex items-center gap-1.5 font-mono-uc text-[9px] uppercase tracking-widest text-amber-400 hover:text-amber-200 px-2.5 py-1.5 border border-amber-500/40 hover:bg-amber-500/10 disabled:opacity-50"
                >
                  {busyId === t.id ? <Loader2 size={10} className="animate-spin"/> : <Zap size={10}/>}
                  {taskRfqs[t.id] ? "RFQ Raised" : "Approve & Execute"}
                </button>
              </div>
              {taskRfqs[t.id] && (
                <SovereignRFQCard
                  rfq={taskRfqs[t.id]}
                  onArbitrageAuthorized={async (r) => { await approve(t.id, r.rfq.id); }}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Executed pipeline */}
      {executed.length > 0 && (
        <div className="space-y-1 mb-4">
          <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/40">Execution Chain</div>
          {executed.map((t) => (
            <div key={t.id} className="flex items-center gap-2 text-[11px] py-1 border-b border-white/5" data-testid={`arb-executed-${t.id}`}>
              <span className={t.status === "delivered" ? "text-emerald-400" : "text-amber-400"}>
                {t.status === "delivered" ? "✓" : "◆"}
              </span>
              <span className="text-white/80 truncate flex-1">{t.title}</span>
              <span className="font-mono-uc text-[9px] uppercase tracking-wider text-white/40">{STATUS_LABEL[t.status]}</span>
            </div>
          ))}
        </div>
      )}

      {/* Ledger */}
      <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/40 mb-1">Controller Ledger</div>
      <div className="bg-black border border-white/10 max-h-56 overflow-y-auto uc-scroll" data-testid="arb-ledger">
        {(summary?.ledger || []).length === 0 && (
          <div className="text-white/40 italic text-xs p-3">No arbitrage revenue yet — run a scan and approve a gig.</div>
        )}
        {(summary?.ledger || []).map((e) => (
          <div key={e.id} className="flex items-center gap-2 px-3 py-2 border-b border-white/5 last:border-none text-[11px]" data-testid={`arb-ledger-${e.id}`}>
            {e.status === "confirmed"
              ? <CheckCircle2 size={12} className="text-emerald-400 flex-none"/>
              : <Landmark size={12} className="text-amber-400 flex-none"/>}
            <span className="text-white/80 truncate flex-1">{e.title}</span>
            <span className="font-mono-uc text-white/40 text-[10px]">{Math.round(e.margin * 100)}%</span>
            <span className={`font-mono-uc font-medium ${e.status === "confirmed" ? "text-emerald-400" : "text-amber-400"}`}>
              +${e.profit.toFixed(2)}
            </span>
            {e.status === "pending_payout" ? (
              <button
                data-testid={`arb-markpaid-${e.id}`}
                onClick={() => markPaid(e.id)}
                className="font-mono-uc text-[9px] uppercase tracking-wider text-emerald-400 hover:text-emerald-200 px-2 py-0.5 border border-emerald-500/40 hover:bg-emerald-500/10"
              >Mark Paid</button>
            ) : (
              <span className="font-mono-uc text-[9px] uppercase tracking-wider text-white/35">CONFIRMED</span>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
